/* 太史慈（字子義）
 * v0.5.0 重製：手戟（近戰）↔ 弓（遠程）雙形態，R「換兵」切換；技能 v1 照 LoL 杰西（docs/FORMS-DESIGN.md）。
 * 史實：「猿臂善射，弦不虛發」；北海突圍時帶兩騎各持一「的」（箭靶）出城；神亭與孫策單挑，奪孫策兜鍪。 */
import { SG, L, gd, along, landing, move, spMul, sigOf } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, P, S, D } from '../define.ts';

// 每個形態的冷卻（形態切換前 u.cds 還沒建，就用目前這組）
const cdsOf = (u: Unit, f: number) => (u.cds ? u.cds[f] : (u.form || 0) === f ? u.cd : [0, 0, 0, 0]);

export default defineHero({
  id: 'taishici', name: '太史慈', sub: '字子義', faction: 'wu', role: '戰士/射手', weapon: 'halberd',
  look: { statRole: '刺客', dual: true, weaponName: '雙戟', fx: '#ffd0a0' },
  // 手戟形態（預設，1 級起；杰西錘形態）＋ R 換兵
  skills: [
    ['神亭搏戰', 'slam', '躍向指定地點，落地時對周圍造成 {60/143/227/310} 物理傷害並緩速 {35/43/52/60}% 2 秒。', { sig: 'skies', dmg: [60, 143, 227, 310], slow: [35, 43, 52, 60], r: 240, aim: P(480, 240), cd: [16, 13.3, 10.7, 8], mp: 40, el: null }],
    ['奪盔', 'buff', '被動：手戟普攻命中回復 {15/18/22/25} 氣。主動：4 秒內每秒對周圍的敵人造成 {35/60/85/110} 法術傷害。', { x: { k: 'field', dps: [35, 60, 85, 110], r: 280, dur: 4 }, sig: 'lfield', mana: [15, 18, 22, 25], aim: S(280), cd: 10, mp: 40, el: 'thunder' }],
    ['手戟擲擊', 'multi', '對單一敵人造成其最大生命 {8/12.7/17.3/22}% 的法術傷害並擊退一大段距離（對野怪最多 {200/367/533/700}）。', { x: { k: 'tblow', hpP: [8, 12.7, 17.3, 22], cap: [200, 367, 533, 700], kb: 480, at: 0.3 }, aim: U(190), cd: [20, 16.7, 13.3, 10], mp: 55, el: null }],
    ['換兵', 'buff', '切換手戟／弓（6 級起）。切成手戟：護甲、法防 +{2/3/4}，下一次普攻多造成 {60/95/130} 法術傷害；切成弓：下一次普攻讓目標護甲 -{25/30/35}% 5 秒。被動「猿臂」：切換後 0.75 秒移速 +9%。', { x: { k: 'form', at: 0.05 }, sig: 'swap', armor: [2, 3, 4], hitDmg: [60, 95, 130], shred: [25, 30, 35], aim: S(0), cd: 6, mp: 0, el: null }],
  ],
  forms: [
    { id: 'ji', name: '手戟', ranged: false },
    { id: 'gong', name: '弓', ranged: true, weapon: 'bow', look: { dual: false, weaponName: '弓' }, skills: [ // 杰西砲形態
      ['弦不虛發', 'bolt', '朝方向射出一箭，撞到第一個敵人或飛到盡頭時爆炸，對周圍造成 {80/148/217/285} 物理傷害。穿過「的」時加速、射程延長到 1280、爆炸範圍變大、傷害 ×1.4。', { x: { k: 'shock', dmg: [80, 148, 217, 285], r: 136, speed: 1160 }, aim: D(840, 56), cd: 8, mp: [55, 63, 72, 80], el: null }],
      ['應弦而倒', 'buff', '接下來 4 秒內的 3 次普攻攻速 +360%，每次造成 {70/83/97/110}% 普攻傷害；重置普攻。', { x: { k: 'hyper', mul: [70, 83, 97, 110], n: 3, dur: 4, as: 360, at: 0.05 }, aim: S(0), cd: [13, 10.3, 7.7, 5], mp: 40, el: null }],
      ['持的出城', 'trap', '在前方立起一排「的」（寬 600，4 秒）：友方武將穿過時移速 +{35/43/52/60}%（3 秒內遞減）；「弦不虛發」穿過時強化。', { x: { k: 'gate', ms: [35, 43, 52, 60], w: 600, dur: 4 }, aim: P(520, 60), cd: 16, mp: 50, el: null }],
    ] },
  ],
  // 電腦：6 級後，敵將在遠處而弓 Q 好了就切弓；被貼身、或敵將殘血而手戟 Q 好了就切手戟
  formAi: ({ u, t, d, hero, form }) => {
    if (!t) return null;
    if (form === 0) return d > 420 && (!hero || cdsOf(u, 1)[0] <= 0) ? 1 : null;
    return d < 260 || (hero && t.hp / t.maxHp < 0.4 && d < 480 && cdsOf(u, 0)[0] <= 0) ? 0 : null;
  },
  sigSkills: {
    // 神亭搏戰（飛躍重擊）：0.1～0.5 飛到落點，0.55 落地
    skies(u, a, k, dt, cross) {
      const sk = a.sk;
      if (!a.to) a.to = landing(u.x, u.y, a.pt[0], a.pt[1]);
      if (k > 0.1 && k < 0.5) { const f = Math.min(1, (k - 0.1) / 0.4); u.x = a.from[0] + (a.to[0] - a.from[0]) * f; u.y = a.from[1] + (a.to[1] - a.from[1]) * f; u.air = true; }
      if (cross(0.5)) { u.x = a.to[0]; u.y = a.to[1]; u.air = false; }
      if (cross(0.55)) {
        const dmg = L(sk.dmg, a.lvl) * spMul(u), slow = L(sk.slow, a.lvl) / 100;
        this.enemiesNear(u, u.x, u.y, sk.r).forEach((t) => {
          if (t.structure) return;
          this.damage(u, t, dmg, { stagger: 0.35 });
          if (t.alive) this.addBuff(t, { id: sk.name, t: 2, neg: true, ms: -slow });
        });
        this.fx({ k: 'slam', x: u.x, y: u.y, r: sk.r, col: u.spec.fx });
      }
    },
  },
  kinds: {
    // 奪盔（閃電力場）：4 秒周圍持續傷害
    field(u, a, X) {
      this.addBuff(u, { id: a.sk.name, t: X.dur, aoe: { r: X.r, dps: X.dps * spMul(u) }, col: '#9fd0ff' });
    },
    // 手戟擲擊（雷霆一擊）：最大生命 % 傷害＋擊退
    tblow(u, a, X) {
      const t = this.xPick(u, a.pt, 150);
      if (!t) { this.xRefund(u, a); return; }
      let dmg = t.maxHp * X.hpP / 100 * spMul(u);
      if (t.kind !== 'hero') dmg = Math.min(dmg, X.cap);
      this.damage(u, t, dmg, { magic: true, spell: true, stagger: 0.4 });
      this.fx({ k: 'spark', x: t.x, y: t.y - 30, col: u.spec.fx });
      if (!t.alive || t.structure || t.kind === 'boss' || t.kind === 'cart') return;
      this.applyCC(u, t, { stun: 0.3 }); // 被擊飛的這段時間不能動
      const dir = SG.dirTo(u, t.x, t.y), n = 8;
      for (let j = 1; j <= n; j++) this.later(0.03 * j, () => { if (t.alive) move(t, (dir[0] * X.kb) / n, (dir[1] * X.kb) / n); });
    },
    // 弦不虛發（電能衝擊）：撞到第一個敵人或飛到盡頭時爆炸；穿過「的」就強化（step.after 掛勾）
    shock(u, a, X) {
      const sp = spMul(u);
      const boom = (p: any) => {
        const mul = p.sup ? 1.4 : 1, r = p.sup ? 200 : X.r;
        this.enemiesNear(u, p.x, p.y, r).forEach((t) => this.damage(u, t, X.dmg * sp * mul * (t.structure ? 0.5 : 1), { stagger: 0.3, sk: true }));
        this.fx({ k: 'boom', x: p.x, y: p.y, r, col: u.spec.fx });
      };
      const p: any = this.shootProj(u, { dir: a.dir, speed: X.speed, range: this.skillRange(a.sk, a.lvl), rad: 22, shape: 'arrow', col: u.spec.fx, big: true, shock: true,
        onHit: () => { boom(p); return true; }, onEnd: () => boom(p) });
    },
    // 應弦而倒（超能電荷）：接下來 3 次普攻
    hyper(u, a, X) {
      u.hyper = { n: X.n, until: this.t + X.dur, mul: X.mul / 100 };
      this.addBuff(u, { id: a.sk.name, t: X.dur, as: X.as });
      u.atkCd = 0;
    },
    // 持的出城（加速閘門）：跟施放方向垂直的一排「的」
    gate(u, a, X) {
      const c = a.pt, pd: [number, number] = [-a.dir[1], a.dir[0]], h = X.w / 2;
      const e0 = along(c[0], c[1], pd, h), e1 = along(c[0], c[1], [-pd[0], -pd[1]], h);
      u.gate = { seg: [e0[0], e0[1], e1[0], e1[1]], until: this.t + X.dur, t0: this.t, ms: X.ms / 100 };
    },
  },
  hooks: [
    // 換兵：換成手戟加護甲（直到再切換）、下一次普攻的效果；猿臂加速
    hook('form', 20, function (u, from, to) {
      const sw = sigOf(u, 'swap');
      if (!sw) return;
      u.swapHit = to === 0 ? { k: 'ji', dmg: sw.v('hitDmg') } : { k: 'gong', shred: sw.v('shred') / 100 };
      this.addBuff(u, { id: '猿臂', t: 0.75, ms: 0.09 });
      this.fx({ k: 'puff', x: u.x, y: u.y });
    }),
    hook('recalc', 40, function (u, sg) {
      if (sg && sg.swap && (u.form || 0) === 0 && u.spec.forms) { const v = sg.swap.v('armor'); u.armor += v; u.mr = (u.mr || 0) + v; }
    }),
    // 普攻命中：奪盔回氣（只在手戟形態）、換兵後的下一擊
    hook('onHit', 25, function (u, t) {
      const lf = sigOf(u, 'lfield');
      if (lf) u.mp = Math.min(u.maxMp, u.mp + lf.v('mana'));
      const s = u.swapHit;
      if (s) {
        u.swapHit = null;
        if (s.k === 'ji') this.damage(u, t, s.dmg, { magic: true, quiet: true });
        else if (!t.structure) this.addBuff(t, { id: '換兵', t: 5, neg: true, armor: -Math.max(0, t.armor) * s.shred });
      }
    }),
    // 應弦而倒：普攻打折／加成，打完 3 次就收
    hook('attack', 45, function (u, a, next) {
      const h = u.hyper;
      if (!h) return next();
      if (h.until < this.t || h.n <= 0) { u.hyper = null; this.addBuff(u, { id: '應弦而倒', t: 0.01 }); return next(); }
      const atk = u.atk; u.atk = atk * h.mul;
      try { next(); } finally { u.atk = atk; }
      if (--h.n <= 0) { u.hyper = null; this.addBuff(u, { id: '應弦而倒', t: 0.01 }); }
    }),
    // 持的出城：友方武將穿過加速（3 秒內遞減，在「的」上就一直刷新）；弦不虛發穿過就強化
    hook('step.after', 30, function (dt) {
      for (const u of this.units) {
        const g = u.gate;
        if (!g) continue;
        if (g.until <= this.t || !u.alive) { u.gate = null; continue; }
        for (const h of this.units) {
          if (h.kind !== 'hero' || !h.alive || h.team !== u.team || SG.segDist(h.x, h.y, g.seg) > 40 + h.r * 0.5) continue;
          h.gateB = { amt: g.ms, t0: this.t };
        }
        for (const p of this.projs) {
          if (!p.shock || p.sup || p.owner !== u || SG.segDist(p.x, p.y, g.seg) > 30) continue;
          p.sup = true;
          const s = Math.hypot(p.vx, p.vy / SG.K) || 1, sp = 1880;
          p.vx = (p.vx / s) * sp; p.vy = (p.vy / s) * sp; p.speed = sp; p.range = 1280;
          this.fx({ k: 'spark', x: p.x, y: p.y, col: '#ffe28a' });
        }
      }
      for (const h of this.units) {
        const b = h.gateB;
        if (!b) continue;
        const k = 1 - (this.t - b.t0) / 3;
        if (k <= 0 || !h.alive) { h.gateB = null; this.addBuff(h, { id: '持的出城', t: 0.01 }); continue; }
        this.addBuff(h, { id: '持的出城', t: 0.2, ms: b.amt * k });
      }
    }),
    hook('revive', 40, function (u) { u.hyper = null; u.gate = null; u.swapHit = null; }),
  ],
  ai: {
    field: ({ t, d, hero }) => hero && d < 300,
    tblow: ({ t, d, hero, hpR }) => hero && d < 220,
    shock: ({ t, d, hero }) => !!t && (hero ? d < 840 : d < 600),
    hyper: ({ u, t, d, hero }) => !!t && (hero || !!t.structure) && d < u.range + 60,
    gate: ({ u, t, d, hero }) => hero && d < 900 && cdsOf(u, 1)[0] <= 0,
  },
  aiSig: {
    skies: ({ u, t }) => !!t && t.kind === 'hero' && gd(u.x, u.y, t.x, t.y) < 500,
  },
});
